import FeedbackChat from '@/components/FeedbackChat';
import { LanguageSwitcher } from '@/components';
import { SelectBot } from 'components/SelectBot';
import { Button } from 'components/shadcn/button';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
} from 'components/shadcn/sidebar';
import { AuthContext } from '@/contexts';
import { useRTL } from '@/hooks';
import { Check, Copy, Edit, MessageCircle } from 'lucide-react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Role } from 'types';
import { apiUrl, cn } from 'utils';

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  const [isCopied, setIsCopied] = useState(false);

  const { role, canManageRag } = useContext(AuthContext);
  const isAdmin = role === Role.ADMIN;

  const isActiveExact = (path: string) => {
    if (!id) return false;
    return location.pathname === `/${id}/${path}`;
  };

  const isActivePrefix = (path: string) => {
    if (!id) return false;
    const base = `/${id}/${path}`;
    return location.pathname === base || location.pathname.startsWith(`${base}/`);
  };

  const showRag = isAdmin || canManageRag;

  const navigationItems = [
    {
      path: 'edit',
      label: t('dashboard.navigation.editor'),
      icon: Edit,
      active: isActivePrefix('edit'),
      children: showRag
        ? [
            {
              path: 'edit',
              label: t('dashboard.navigation.mainEditor'),
              active: isActiveExact('edit'),
            },
            {
              path: 'edit/rag',
              label: t('dashboard.chatForm.dialogs.ragTitle'),
              active: isActivePrefix('edit/rag'),
            },
          ]
        : [],
    },
    {
      path: 'conversations',
      label: t('dashboard.navigation.conversations'),
      icon: MessageCircle,
      active: isActivePrefix('conversations'),
      children: [],
    },
  ];

  const handleCopyLink = () => {
    const link = `${window.location.origin}/${id}`;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        setIsCopied(true);
        toast.success(t('dashboard.toasts.linkCopied'));
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch((err) => {
        toast.error(t('dashboard.toasts.linkCopyFailed'));
        console.error('Failed to copy: ', err);
      });
  };

  return (
    <>
      <header className="fixed top-0 left-0 w-full z-50 bg-background flex h-16 items-center gap-2 border-b px-2">
        <div className="flex w-full items-center justify-between">
          <SelectBot width="w-48" />
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={handleCopyLink}>
              <div className="relative h-4 w-4 me-2">
                <Copy
                  className={cn(
                    'absolute h-4 w-4 transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)]',
                    isCopied ? 'scale-[0.25] opacity-0 blur-[4px]' : 'scale-100 opacity-100 blur-0',
                  )}
                />
                <Check
                  className={cn(
                    'absolute h-4 w-4 transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)]',
                    isCopied ? 'scale-100 opacity-100 blur-0' : 'scale-[0.25] opacity-0 blur-[4px]',
                  )}
                />
              </div>
              {t('common.copyLinkToChat')}
            </Button>
            <Button size="sm" onClick={() => navigate(`/${id}`)}>
              {t('common.chatWithBot')}
            </Button>
          </div>
        </div>
      </header>
      <SidebarProvider>
        {/* mt-16 for the header space */}
        <Sidebar className="mt-16" side={isRTL ? 'right' : 'left'}>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          disabled={!id}
                          onClick={() => navigate(`/${id}/${item.path}`)}
                          isActive={item.active}
                          className="cursor-pointer"
                          size="lg"
                        >
                          <Icon className="h-4 w-4" />
                          <span>{item.label}</span>
                        </SidebarMenuButton>

                        {!!item.children?.length && (
                          <SidebarMenuSub className="cursor-pointer">
                            {item.children.map((child) => (
                              <SidebarMenuSubItem key={child.path}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={child.active}
                                  aria-disabled={!id}
                                  onClick={() => navigate(`/${id}/${child.path}`)}
                                >
                                  <span>{child.label}</span>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        )}
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          {/* mt-16 for the header space */}
          <SidebarFooter className="mb-16">
            <FeedbackChat />
          </SidebarFooter>
        </Sidebar>

        {/* mt-16 for the header space */}
        <SidebarInset className="mt-16">
          {/* Content */}
          <Outlet />
        </SidebarInset>
      </SidebarProvider>
    </>
  );
};

export default DashboardLayout;
